import { useState, useEffect, useCallback } from "react";

/**
 * 自动计算页面顶部所有 Fixed/Sticky 元素的高度总和
 * @param offset 额外的偏移量 (可选，默认 0)
 */
export function useDynamicStickyTop(offset: number = 0) {
  const [stickyTop, setStickyTop] = useState(0);

  const calculateTop = useCallback(() => {
    // 1. 获取所有通过标签名可能的头部元素，以及所有显式设置了 sticky/fixed 类的元素
    // 你可以根据项目习惯添加更多选择器，比如 '.ant-layout-header'
    const candidates = Array.from(
      document.querySelectorAll(
        "header, nav, div[class*='sticky'], div[class*='fixed']"
      )
    ) as HTMLElement[];

    let occupiedSpace = 0;
    // 用于记录已经通过的区域，避免重叠计算
    const coverage: { top: number; bottom: number }[] = [];

    candidates.forEach((el) => {
      const style = window.getComputedStyle(el);
      const rect = el.getBoundingClientRect();

      // 2. 核心过滤条件：
      // - 必须是 fixed 或 sticky
      // - 必须可见 (height > 0)
      // - 必须贴在顶部 (top < 10, 给一点容错空间)
      const isPositioned =
        style.position === "fixed" || style.position === "sticky";
      const isVisible =
        rect.height > 0 &&
        style.display !== "none" &&
        style.visibility !== "hidden";
      const isAtTop = rect.top <= 10; // 允许微小的偏移

      if (isPositioned && isVisible && isAtTop) {
        // 3. 计算有效高度 (处理层叠情况)
        // 简单的逻辑是直接加高度，但这会导致如果两个 fixed 元素重叠，高度会算两遍
        // 这里采用简化的逻辑：取最大的那个底边距离 (bottom) 作为当前的 stickyTop

        // 如果你需要非常精确的层叠计算，逻辑会很复杂。
        // 通常 header layout 是垂直堆叠的，所以直接看最大的 rect.bottom 往往是最准确的近似值。
        if (rect.bottom > occupiedSpace) {
          occupiedSpace = rect.bottom;
        }
      }
    });

    setStickyTop(occupiedSpace + offset);
  }, [offset]);

  useEffect(() => {
    // 初始计算
    calculateTop();

    // 监听滚动 (sticky 元素的状态可能会随滚动改变，虽然高度通常不变，但为了保险)
    window.addEventListener("scroll", calculateTop);
    // 监听窗口大小改变
    window.addEventListener("resize", calculateTop);

    // 监听 DOM 变化 (防止 header 是后续异步加载进来的)
    const observer = new MutationObserver(calculateTop);
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["class", "style"],
    });

    return () => {
      window.removeEventListener("scroll", calculateTop);
      window.removeEventListener("resize", calculateTop);
      observer.disconnect();
    };
  }, [calculateTop]);

  return stickyTop;
}
